// src/screens/student/resourceslibrary/ResourcesLibrary.js
import React, { useState, useContext, useEffect } from "react";
import axios from "axios";
import AppContext from "../../../context/AppContext";
import "../../../assets/css/student/resourceslibrary/ResourcesLibrary.css";
import StudentLayout from "../../../layouts/student/studentlayout/StudentLayout";
import { Table } from "react-bootstrap";

const ResourcesLibrary = () => {
  const { baseUrl, loggedInStudent } = useContext(AppContext);
  const [resources, setResources] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [group, setGroup] = useState("");
  const [description, setDescription] = useState("");
  const [studyGroup, setStudyGroup] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null); // null = no group selected
  const [searchQuery, setSearchQuery] = useState("");
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [sortBy, setSortBy] = useState("recent");
  const [viewMode, setViewMode] = useState("grid");

  const viewAllStudyGroups = () => {
    const formData = new FormData();
    formData.append("chstudent_id",loggedInStudent.id);
    axios.post(`${baseUrl}/student/studygroup/viewAllStudyGroups`,formData,{
       headers: { "Content-Type": "multipart/form-data" }
    })
      .then((response) => {
        setStudyGroup(response.data.studygroups);
      })
      .catch((error) => console.error(error));
  };

  const viewAllResources = () => {
    axios.post(`${baseUrl}/student/resourceLibrary/viewAllResources`)
      .then((response) => setResources(response.data.resources))
      .catch((error) => console.error(error));
  };

  const uploadResource = () => {
    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("chstudygroup_id", group);
    formData.append("description", description);
    formData.append("student_id", loggedInStudent.id);

    axios.post(`${baseUrl}/student/resourceLibrary/uploadResource`, formData, {
      headers: { "Content-Type": "multipart/form-data" }
    })
      .then(() => {
        setShowUploadModal(false);
        viewAllResources();
      })
      .catch((error) => console.error(error));
  };

  const downloadResource = (id, fileName) => {
    axios.post(`${baseUrl}/student/resourceLibrary/downloadResource`, { id }, { responseType: "blob" })
      .then((response) => {
        const blob = new Blob([response.data], { type: response.headers['content-type'] });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch((error) => console.error(error));
  };

  const deleteResource = (id) => {
    if (window.confirm("Are you sure you want to delete this file?")) {
      axios.post(`${baseUrl}/student/resourceLibrary/deleteResource`, { id })
        .then(() => viewAllResources())
        .catch((error) => console.error(error));
    }
  };

  const handleUpload = (e) => { e.preventDefault(); uploadResource(); };

  // Filter resources by selected group
  const filteredResources = selectedGroup
    ? resources.filter((resource) => {
        const matchesGroup = resource.chstudygroup?.id === selectedGroup;
        const matchesSearch = resource.fileName?.toLowerCase().includes(searchQuery.toLowerCase());
        return matchesGroup && matchesSearch;
      })
    : [];

  const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = () => setIsDragging(false);
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    setSelectedFile(e.dataTransfer.files[0]);
  };

  useEffect(() => {
    viewAllResources();
    viewAllStudyGroups();
  }, []);

  // Get selected group name
  const selectedGroupName = studyGroup.find(g => g.id === selectedGroup)?.groupName || "";

  return (
    <StudentLayout>
      <div className="resources-library">
        {/* Header */}
        <div className="page-header">
          <div className="page-title-section">
            <div className="page-title-icon">📚</div>
            <div>
              <h1 className="page-title">Resources Library</h1>
              <p className="page-description">Share and access study materials</p>
            </div>
          </div>
          <button className="btn btn-primary" onClick={() => setShowUploadModal(true)}>
            <span>⬆️</span> Upload Resource
          </button>
        </div>

        {/* ✅ Study Groups Cards */}
        <div className="mt-4">
          <h5 className="mb-3" style={{ fontWeight: "600", color: "#333" }}>📂 My Study Groups</h5>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "12px" }}>
            {studyGroup.length === 0 ? (
              <p style={{ color: "#888" }}>No study groups found.</p>
            ) : (
              studyGroup.map((g) => (
                <div
                  key={g.id}
                  onClick={() => setSelectedGroup(g.id)}
                  style={{
                    padding: "14px 22px",
                    borderRadius: "10px",
                    cursor: "pointer",
                    border: selectedGroup === g.id ? "2px solid #4f46e5" : "2px solid #e0e0e0",
                    backgroundColor: selectedGroup === g.id ? "#4f46e5" : "#fff",
                    color: selectedGroup === g.id ? "#fff" : "#333",
                    fontWeight: "500",
                    fontSize: "14px",
                    boxShadow: selectedGroup === g.id ? "0 4px 12px rgba(79,70,229,0.3)" : "0 2px 6px rgba(0,0,0,0.06)",
                    transition: "all 0.2s ease",
                  }}
                >
                  👥 {g.groupName}
                </div>
              ))
            )}
          </div>
        </div>

        {/* ✅ Resources Table - only show when group is selected */}
        <div className="mt-5">
          {selectedGroup === null ? (
            // No group selected yet
            <div className="empty-state">
              <div className="empty-icon">👆</div>
              <h3>Select a Study Group</h3>
              <p>Click on any group above to see its resources</p>
            </div>
          ) : (
            <>
              {/* Search bar */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
                <h5 style={{ fontWeight: "600", margin: 0 }}>
                  📄 Resources — <span style={{ color: "#4f46e5" }}>{selectedGroupName}</span>
                </h5>
                <div className="search-box">
                  <span className="search-icon">🔍</span>
                  <input
                    type="text"
                    placeholder="Search files..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>

              {filteredResources.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">📭</div>
                  <h3>No resources found</h3>
                  <p>Upload a resource for this group</p>
                </div>
              ) : (
                <Table striped bordered hover>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>File Name</th>
                      <th>Group Name</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredResources.map((resource, index) => (
                      <tr key={resource.id}>
                        <td>{index + 1}</td>
                        <td>{resource.fileName}</td>
                        <td>{resource.chstudygroup?.groupName || "—"}</td>
                        <td>
                          <button className="btn btn-sm btn-primary" onClick={() => downloadResource(resource.id, resource.fileName)}>Download</button>
                          <button className="btn btn-sm btn-danger ms-2" onClick={() => deleteResource(resource.id)}>Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </>
          )}
        </div>

        {/* Upload Modal */}
        {showUploadModal && (
          <div className="modal-overlay" onClick={() => setShowUploadModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Upload Resource</h2>
                <button className="modal-close" onClick={() => setShowUploadModal(false)}>✕</button>
              </div>

              <form onSubmit={handleUpload} className="upload-form">
                <div className={`upload-area ${isDragging ? "dragging" : ""}`}
                     onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}>
                  <div className="upload-icon">📤</div>
                  <h3>Drag & Drop files here</h3>
                  <p>or click to browse</p>
                  <input type="file" className="file-input" onChange={(e) => setSelectedFile(e.target.files[0])}/>
                  <div className="file-types">Supported: PDF, DOC, PPT, MP4, JPG, PNG, ZIP</div>
                  <h3>Selected file : {selectedFile ? selectedFile.name : "No file selected"}</h3>
                </div>

                <div className="form-group">
                  <label>Study Group *</label>
                  <select value={group} onChange={(e) => setGroup(Number(e.target.value))}>
                    <option value="">Select Group</option>
                    {studyGroup.map((g) => (
                      <option key={g.id} value={g.id}>{g.groupName}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Description (Optional)</label>
                  <textarea
                    placeholder="Add a description for this resource..."
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows="3"
                  />
                </div>

                <div className="modal-actions">
                  <button type="button" className="btn btn-outline" onClick={() => setShowUploadModal(false)}>Cancel</button>
                  <button type="submit" className="btn btn-primary">Upload Resource</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </StudentLayout>
  );
};

export default ResourcesLibrary;